import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  SelectDS,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DataGridPremiumProps,
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import "../../i18n/i18n";
import {
  companiesListAsync,
  selectCompaniesList,
  selectCompaniesListStatus,
} from "../companiesSlice";
import { companyAccountDataAsync } from "../companiesData/accountDataSlice";
import { VehicleDisplayed } from "./telemetryApi";
import { formatDateWithHourMinute } from "../../lib/materialUiFormats";
import { UpdateDeviceIdCell } from "./components/updateDeviceIdCell";

type VehiclesTableProps = {
  vehiclesListWithId: (VehicleDisplayed & { id: string })[];
  pageName: string;
  pageTitle: string;
  action?: React.ReactNode;
  rowsChecked?: VehicleDisplayed[];
  setRowsChecked?: (rows: VehicleDisplayed[]) => void;
  canEditDeviceId?: boolean;
} & Omit<DataGridPremiumProps, "rows" | "columns">;

export const VehiclesTable = ({
  vehiclesListWithId,
  pageName,
  pageTitle,
  action,
  rowsChecked,
  setRowsChecked,
  canEditDeviceId,
}: VehiclesTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companiesList = useAppSelector(selectCompaniesList);
  const companiesListStatus = useAppSelector(selectCompaniesListStatus);
  const [listDisplayed, setListDisplayed] = useState<VehicleDisplayed[]>([]);
  const [companySelected, setCompanySelected] = useState<string>("");

  const optionCompany = useMemo(
    () =>
      companiesList.map((company) => {
        return {
          label: company.name,
          value: company.uuid,
        };
      }),
    [companiesList],
  );

  const valueOptionCompany = useMemo(
    () => optionCompany.find((option) => option.value === companySelected),
    [companySelected, optionCompany],
  );

  const apiRef = useGridApiRef();

  const aggregation = {
    model: {
      registrationNumber: "size",
      identifier: "size",
      isEligible: "size",
      connectionStatus: "size",
      geolocationError: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    pageTitle,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: vehiclesListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const, "deliveryDate" as const],
  };

  const columnsDisplayed: {
    [index in keyof VehicleDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 200,
      },
      identifier: {
        type: "string",
        width: 200,
      },
      deviceId: {
        type: "string",
        width: 200,
        renderCell: (props: GridRenderCellParams<VehicleDisplayed>) => {
          if (props.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) return;
          if (canEditDeviceId) {
            return <UpdateDeviceIdCell cellProps={props} />;
          }

          return props.value as string;
        },
      },
      isEligible: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => (
          <TextCapitalized>{value}</TextCapitalized>
        ),
      },
      connectionStatus: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => (
          <TextCapitalized>{value}</TextCapitalized>
        ),
      },
      dataProvider: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return (
            <TextCapitalized>
              {value === null || value === undefined
                ? t("vehiclesOfACompany.column.null")
                : value}
            </TextCapitalized>
          );
        },
      },
      geolocationError: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return (
            <TextCapitalized>
              {value === null || value === undefined ? "" : value}
            </TextCapitalized>
          );
        },
      },
      brand: {
        type: "string",
        width: 200,
      },
      createdAt: {
        type: "string",
        width: 250,
        valueFormatter: (value: string | null) => {
          return value ? formatDateWithHourMinute(value) : null;
        },
      },
      department: {
        type: "string",
        width: 200,
      },
    };
  }, [t, canEditDeviceId]);

  useEffect(() => {
    if (companiesListStatus === "idle") {
      void dispatch(companiesListAsync());
    }
  }, [companiesListStatus, dispatch]);

  useEffect(() => {
    if (params.id && !companySelected) {
      setCompanySelected(params.id);
    }
  }, [companySelected, params.id]);

  useEffect(() => {
    if (
      vehiclesListWithId &&
      companiesList &&
      companiesListStatus === "success"
    ) {
      setListDisplayed(vehiclesListWithId);
    }
  }, [vehiclesListWithId, companiesList, companiesListStatus]);

  useEffect(() => {
    if (companySelected) {
      void dispatch(companyAccountDataAsync({ companyUuid: companySelected }));
      navigate(`/home/telemetry/${params.tab}/${companySelected}`);
    }
  }, [companySelected, dispatch, navigate, params.tab]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <StyledContainer>
      <Spacer y={1} />
      <StyledHeader>
        <TextCapitalized>{t("accountData.select.company")}</TextCapitalized>
        <SelectDS
          label={""}
          value={valueOptionCompany}
          options={optionCompany}
          onChange={(selectedOption) =>
            setCompanySelected(selectedOption ? selectedOption.value : "")
          }
          allWidth
        />
      </StyledHeader>
      {action && (
        <>
          <Spacer y={1} />
          {action}
          <Spacer y={1} />
        </>
      )}
      <MaterialUiTable<keyof VehicleDisplayed, VehicleDisplayed>
        rowsChecked={rowsChecked}
        setRowsChecked={setRowsChecked}
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 30rem;
  z-index: 10;
`;
